import React, { useContext, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Nav, Form, Button, Offcanvas } from 'react-bootstrap';
import { ThemeContext } from '../utils/ThemeContext';
import { useAuth } from '../utils/AuthContext';

const Layout = () => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
    setShowMobileMenu(false);
  };

  const handleNavClick = () => {
    setShowMobileMenu(false);
  };

  // Get current page title for mobile header
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return 'Dashboard';
    if (path.includes('desktop-record')) return 'Transcripts';
    if (path.includes('upload')) return 'Upload';
    if (path.includes('recording')) return 'Recording';
    if (path.includes('analytics')) return 'Analytics';
    if (path.includes('people')) return 'People';
    if (path.includes('tasks')) return 'Tasks';
    if (path.includes('documents')) return 'Documents';
    if (path.includes('usage')) return 'Usage';
    if (path.includes('profile')) return 'Profile';
    if (path.includes('summary')) return 'Summary';
    return 'Echo Brief';
  };
  
  // Navigation items - reusable for both desktop and mobile
  const NavItems = ({ onClick }) => (
    <>
      <NavLink 
        to="/" 
        className={({ isActive }) => 
          `nav-link d-flex align-items-center p-3 mb-2 ${isActive ? 'active' : ''}`
        }
        end
        onClick={onClick}
      >
        <i className="bi bi-house-door me-3"></i>
        <span>Dashboard</span>
      </NavLink>
      <NavLink
        to="/desktop-record"
        className={({ isActive }) => 
          `nav-link d-flex align-items-center p-3 mb-2 ${isActive ? 'active' : ''}`
        }
        onClick={onClick}
      >
        <i className="bi bi-file-text me-3"></i>
        <span>Transcripts</span>
      </NavLink>
      <NavLink 
        to="/upload" 
        className={({ isActive }) => 
          `nav-link d-flex align-items-center p-3 mb-2 ${isActive ? 'active' : ''}`
        }
        onClick={onClick}
      >
        <i className="bi bi-upload me-3"></i>
        <span>Upload</span>
      </NavLink>
      <NavLink 
        to="/enhanced-recording" 
        className={({ isActive }) => 
          `nav-link d-flex align-items-center p-3 mb-2 ${isActive ? 'active' : ''}`
        }
        onClick={onClick}
      >
        <i className="bi bi-mic-fill me-3"></i>
        <span>Recording</span>
      </NavLink>
      <NavLink 
        to="/analytics" 
        className={({ isActive }) => 
          `nav-link d-flex align-items-center p-3 mb-2 ${isActive ? 'active' : ''}`
        }
        onClick={onClick}
      >
        <i className="bi bi-bar-chart-fill me-3"></i>
        <span>Analytics</span>
      </NavLink>
      <NavLink 
        to="/people" 
        className={({ isActive }) => 
          `nav-link d-flex align-items-center p-3 mb-2 ${isActive ? 'active' : ''}`
        }
        onClick={onClick}
      >
        <i className="bi bi-people-fill me-3"></i>
        <span>People</span>
      </NavLink>
      <NavLink 
        to="/tasks" 
        className={({ isActive }) => 
          `nav-link d-flex align-items-center p-3 mb-2 ${isActive ? 'active' : ''}`
        }
        onClick={onClick}
      >
        <i className="bi bi-check2-square me-3"></i>
        <span>Tasks</span>
      </NavLink>
      <NavLink 
        to="/documents" 
        className={({ isActive }) => 
          `nav-link d-flex align-items-center p-3 mb-2 ${isActive ? 'active' : ''}`
        }
        onClick={onClick}
      >
        <i className="bi bi-files me-3"></i>
        <span>Documents</span>
      </NavLink>
      <NavLink 
        to="/usage" 
        className={({ isActive }) => 
          `nav-link d-flex align-items-center p-3 mb-2 ${isActive ? 'active' : ''}`
        }
        onClick={onClick}
      >
        <i className="bi bi-speedometer2 me-3"></i>
        <span>Usage</span>
      </NavLink>
      <NavLink 
        to="/profile" 
        className={({ isActive }) => 
          `nav-link d-flex align-items-center p-3 mb-2 ${isActive ? 'active' : ''}`
        }
        onClick={onClick}
      >
        <i className="bi bi-person-fill me-3"></i>
        <span>Profile</span>
      </NavLink>
    </>
  );
  
  return (
    <div className="d-flex flex-column flex-md-row h-100">
      {/* Mobile Header */}
      <div className="mobile-header d-md-none">
        <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-white">
          <Button 
            variant="link" 
            className="p-0 text-dark"
            onClick={() => setShowMobileMenu(true)}
          >
            <i className="bi bi-list" style={{ fontSize: '1.5rem' }}></i>
          </Button>
          <h5 className="mb-0 fw-bold">{getPageTitle()}</h5>
          <div style={{ width: '24px' }}></div> {/* Spacer for centering */}
        </div>
      </div>

      {/* Mobile Offcanvas Menu */}
      <Offcanvas 
        show={showMobileMenu} 
        onHide={() => setShowMobileMenu(false)}
        className="mobile-menu"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <span className="app-logo">Echo Brief</span>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column">
          <Nav className="flex-column flex-grow-1">
            <NavItems onClick={handleNavClick} />
          </Nav>
          
          {/* User info in mobile menu */}
          <div className="mt-auto pt-3 border-top">
            {currentUser && (
              <div className="user-info mb-3">
                <div className="d-flex align-items-center mb-3">
                  <div className="avatar-circle me-3" style={{ width: '40px', height: '40px', fontSize: '1rem' }}>
                    {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div>
                    <div className="fw-bold">{currentUser.name}</div>
                    <div className="text-muted small">{currentUser.email}</div>
                  </div>
                </div>
                <Button 
                  variant="outline-danger" 
                  size="sm" 
                  className="w-100"
                  onClick={handleLogout}
                >
                  <i className="bi bi-box-arrow-right me-2"></i>
                  Logout
                </Button>
              </div>
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Desktop Sidebar - hidden on mobile */}
      <div className="sidebar d-none d-md-flex flex-column">
        {/* Fixed Header - Logo */}
        <div className="sidebar-header py-4 text-center border-bottom">
          <h2 className="app-logo">Echo Brief</h2>
        </div>
        
        {/* Scrollable Nav Section */}
        <div className="sidebar-nav-scroll flex-grow-1 overflow-auto p-3">
          <Nav className="flex-column">
            <NavItems onClick={() => {}} />
          </Nav>
        </div>
        
        {/* Fixed Footer - Profile & Theme */}
        <div className="sidebar-footer border-top p-3">
          {currentUser && (
            <div className="user-info mb-3 text-center">
              <div className="user-avatar mb-2">
                <div className="avatar-circle">
                  {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                </div>
              </div>
              <div className="user-name">{currentUser.name}</div>
              <div className="user-email text-muted small mb-3">{currentUser.email}</div>
              <Button 
                variant="outline-danger" 
                size="sm" 
                className="w-100"
                onClick={handleLogout}
              >
                <i className="bi bi-box-arrow-right me-2"></i>
                Logout
              </Button>
            </div>
          )}
          <div className="theme-toggle d-flex align-items-center justify-content-center mb-3">
            <i className="bi bi-sun me-2"></i>
            <Form.Check 
              type="switch"
              id="theme-switch"
              checked={theme === 'dark'}
              onChange={toggleTheme}
              aria-label="Toggle theme"
              className="theme-switch"
            />
            <i className="bi bi-moon ms-2"></i>
          </div>
          <p className="text-muted small text-center mb-0">© 2025 Echo Brief</p>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="main-content flex-grow-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
};

export default Layout;
